/**
 * AdminUsersTab Component
 * User management tab for admin panel with delete functionality
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Search, Mail, Calendar, Trophy, Clock, Trash2, Shield } from 'lucide-react';

const AdminUsersTab = ({
  users,
  searchTerm,
  setSearchTerm,
  formatDate,
  handleDeleteUser
}) => {
  const filteredUsers = (users || []).filter(u =>
    (u.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (u.name?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <Card className="bg-white dark:bg-slate-900">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle>Registered Participants</CardTitle>
          <div className="flex gap-2 items-center">
            <Badge variant="outline" className="text-sm">
              {(users || []).length} Total Users
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-users-input"
          />
        </div>

        <ScrollArea className="h-[500px] rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Track</TableHead>
                <TableHead>Access Until</TableHead>
                <TableHead>Streak</TableHead>
                <TableHead>Badges</TableHead>
                <TableHead>Registered</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{u.name || 'Unknown'}</p>
                            {u.is_admin && (
                              <Badge className="bg-purple-600 text-white text-xs">
                                <Shield className="w-3 h-3 mr-1" />
                                Admin
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {u.purchased_tracks?.length > 0 ? (
                        <div className="flex gap-1 flex-wrap">
                          {u.purchased_tracks.map((track) => (
                            <Badge 
                              key={track} 
                              variant="outline"
                              className={track === 'PMP' ? 'border-blue-500 text-blue-600' : 'border-green-500 text-green-600'}
                            >
                              {track}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.access_expires ? (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Clock className="w-3 h-3" />
                          {formatDate(u.access_expires)}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-lg">🔥</span>
                        <span className="font-medium">{u.streak_count || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <span>{u.badges?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar className="w-3 h-3" />
                        {formatDate(u.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {u.is_admin ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled
                          className="text-slate-300"
                          title="Admin users cannot be deleted"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the user <strong>{u.name || u.email}</strong>?
                                <br /><br />
                                This will permanently delete:
                                <ul className="list-disc list-inside mt-2 text-left">
                                  <li>User account and profile</li>
                                  <li>All exam attempts and results</li>
                                  <li>Gamification progress (badges, streak)</li>
                                  <li>Purchased course access</li>
                                </ul>
                                <br />
                                <strong className="text-red-600">This action cannot be undone.</strong>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(u.user_id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete User
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
        <div className="mt-4 text-sm text-slate-500">
          {filteredUsers.length} of {users.length} users shown
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminUsersTab;
